(() => {
  const elementName = 'nuora-snap-carousel';

  if (!('customElements' in window)) return;
  if (customElements.get(elementName)) return;

  class NuoraSnapCarousel extends HTMLElement {
    constructor() {
      super();
      this.handleClick = this.handleClick.bind(this);
      this.handleScroll = this.handleScroll.bind(this);
      this.handleResize = this.handleResize.bind(this);
      this.handleBlockSelect = this.handleBlockSelect.bind(this);
      this.sync = this.sync.bind(this);
    }

    connectedCallback() {
      if (this.isConnectedToCarousel || this.ipInitPending) return;

      // IP-2026-07-14-change: target carousels opt into near-viewport setup, while all non-target usages retain their original eager initialization.
      const shouldDefer = this.hasAttribute('data-ip-defer-init')
        && !window.Shopify?.designMode
        && 'IntersectionObserver' in window;

      if (!shouldDefer) {
        this.initializeCarousel();
        return;
      }

      this.ipInitPending = true;
      this.initObserver = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.initObserver?.disconnect();
        this.initObserver = null;
        if (!this.isConnected) return;
        this.initializeCarousel();
      }, { rootMargin: '800px 0px' });
      // IP-2026-07-14-change: observe the section's intrinsic box so content-visibility does not hide the descendant trigger geometry.
      this.initObserver.observe(this.closest('[data-nuora-below-fold], .nuora-photo-proof') || this);
    }

    // IP-2026-07-14-change: postpone scrollWidth/getBoundingClientRect work for contained target sections until it can affect the visitor.
    initializeCarousel() {
      if (this.isConnectedToCarousel) return;
      this.ipInitPending = false;

      this.track = this.querySelector('[data-nuora-carousel-track]');
      this.items = Array.from(this.querySelectorAll('[data-nuora-carousel-item]'));
      this.dots = Array.from(this.querySelectorAll('[data-nuora-carousel-dot]'));
      this.previousButton = this.querySelector('[data-nuora-carousel-prev]');
      this.nextButton = this.querySelector('[data-nuora-carousel-next]');

      if (!this.track || !this.items.length) return;

      this.isConnectedToCarousel = true;
      this.loop = this.hasAttribute('data-loop') && this.getAttribute('data-loop') !== 'false';
      this.reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
      this.scrollTimer = 0;
      this.resizeFrame = 0;

      this.addEventListener('click', this.handleClick);
      this.track.addEventListener('scroll', this.handleScroll, { passive: true });
      window.addEventListener('resize', this.handleResize, { passive: true });
      document.addEventListener('shopify:block:select', this.handleBlockSelect);

      this.setAttribute('data-nuora-carousel-ready', '');
      this.sync();
    }

    disconnectedCallback() {
      // IP-2026-07-14-change: disconnect pending viewport work when a target section is removed before initialization.
      if (this.initObserver) {
        this.initObserver.disconnect();
        this.initObserver = null;
      }
      this.ipInitPending = false;

      if (!this.isConnectedToCarousel) return;

      this.removeEventListener('click', this.handleClick);
      this.track?.removeEventListener('scroll', this.handleScroll);
      window.removeEventListener('resize', this.handleResize);
      document.removeEventListener('shopify:block:select', this.handleBlockSelect);
      window.clearTimeout(this.scrollTimer);
      window.cancelAnimationFrame(this.resizeFrame);
      this.isConnectedToCarousel = false;
    }

    get scrollInset() {
      const styles = window.getComputedStyle(this.track);
      return Number.parseFloat(styles.scrollPaddingInlineStart || styles.scrollPaddingLeft) || 0;
    }

    get nearestIndex() {
      const trackStart = this.track.getBoundingClientRect().left + this.scrollInset;

      return this.items.reduce(
        (nearest, item, index) => {
          const distance = Math.abs(item.getBoundingClientRect().left - trackStart);
          return distance < nearest.distance ? { index, distance } : nearest;
        },
        { index: 0, distance: Number.POSITIVE_INFINITY }
      ).index;
    }

    get canScroll() {
      return this.track.scrollWidth > this.track.clientWidth + 1;
    }

    handleClick(event) {
      const dot = event.target.closest('[data-nuora-carousel-dot]');
      if (dot && this.contains(dot)) {
        this.goTo(Number.parseInt(dot.dataset.index, 10) || 0);
        return;
      }

      const previous = event.target.closest('[data-nuora-carousel-prev]');
      if (previous && this.contains(previous)) {
        this.step(-1);
        return;
      }

      const next = event.target.closest('[data-nuora-carousel-next]');
      if (next && this.contains(next)) this.step(1);
    }

    handleScroll() {
      window.clearTimeout(this.scrollTimer);
      this.scrollTimer = window.setTimeout(this.sync, 80);
    }

    handleResize() {
      window.cancelAnimationFrame(this.resizeFrame);
      this.resizeFrame = window.requestAnimationFrame(this.sync);
    }

    handleBlockSelect(event) {
      const blockId = event.detail?.blockId;
      let selectedItem = blockId
        ? this.items.find((item) => item.dataset.blockId === blockId)
        : null;

      if (!selectedItem) {
        selectedItem = event.target?.closest?.('[data-nuora-carousel-item]');
      }

      if (!selectedItem || !this.contains(selectedItem)) return;
      this.goTo(this.items.indexOf(selectedItem), true);
    }

    step(direction) {
      let targetIndex = this.nearestIndex + direction;

      if (this.loop) {
        targetIndex = (targetIndex + this.items.length) % this.items.length;
      }

      this.goTo(targetIndex);
    }

    goTo(index, instant = false) {
      const boundedIndex = Math.max(0, Math.min(this.items.length - 1, index));
      const item = this.items[boundedIndex];
      if (!item) return;

      const trackRect = this.track.getBoundingClientRect();
      const itemRect = item.getBoundingClientRect();
      const left = this.track.scrollLeft + itemRect.left - trackRect.left - this.scrollInset;

      this.track.scrollTo({
        left,
        behavior: instant || this.reduceMotion ? 'auto' : 'smooth'
      });
      this.updateState(boundedIndex);
    }

    sync() {
      this.updateState(this.nearestIndex);
    }

    updateState(index) {
      const scrollable = this.canScroll;
      this.toggleAttribute('data-nuora-carousel-scrollable', scrollable);

      this.items.forEach((item, itemIndex) => {
        item.toggleAttribute('data-active', itemIndex === index);
      });

      this.dots.forEach((dot) => {
        const active = Number.parseInt(dot.dataset.index, 10) === index;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-current', active ? 'true' : 'false');
      });

      if (this.previousButton) {
        this.previousButton.disabled = !scrollable;
      }

      if (this.nextButton) {
        this.nextButton.disabled = !scrollable;
      }
    }
  }

  customElements.define(elementName, NuoraSnapCarousel);
})();
